import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Calendar, Settings, Clock, RefreshCw, Loader2, Building2 } from 'lucide-react';
import apiClient from '@/services/apiClient';
import { motion } from 'framer-motion';

export interface Schedule {
    id: string;
    schedule_name: string;
    generation_day: number;
    due_day: number;
    properties: { id: string; name: string }[];
    fixed_charges: { description: string; amount: number }[];
    status: 'ACTIVE' | 'PAUSED';
    last_run?: string;
    next_run?: string;
    metrics?: {
        total_tenants: number;
        estimated_total: number;
    };
}

export const ManageBillingSchedules: React.FC = () => {
    const navigate = useNavigate();

    const [schedules, setSchedules] = useState<Schedule[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchSchedules = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await apiClient.get<Schedule[]>("/billing/schedules");
            setSchedules(response.data);
        } catch (err: any) {
            console.error(err);
            setError(err.response?.data?.error || "Failed to fetch billing schedules.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchSchedules();
    }, [fetchSchedules]);

    const handleSchedulePress = (scheduleId: string) => {
        // We can add an edit schedule screen later
        console.log("Navigate to edit schedule:", scheduleId);
    };

    return (
        <div className="max-w-6xl mx-auto pb-12 space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between bg-light-card dark:bg-dark-card p-4 rounded-lg shadow-sm border border-light-border dark:border-dark-border">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate('/landlord/financials')}
                        className="p-2 rounded-full hover:bg-light-bg dark:hover:bg-dark-bg transition-colors"
                    >
                        <ArrowLeft size={24} className="text-primary dark:text-golden" />
                    </button>
                    <div>
                        <h1 className="text-xl font-bold text-light-text dark:text-dark-text">Automated Billing</h1>
                        <p className="text-sm text-light-subtle dark:text-dark-subtle">Manage recurring invoices</p>
                    </div>
                </div>
                <button
                    onClick={() => navigate('/landlord/billing/schedules/new')}
                    className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-white px-4 py-2 rounded-lg transition-colors shadow-sm"
                >
                    <Plus size={20} />
                    <span className="hidden sm:inline font-medium">New Schedule</span>
                </button>
            </div>

            {/* List Content */}
            {loading ? (
                <div className="flex h-[40vh] items-center justify-center">
                    <Loader2 className="w-8 h-8 text-primary animate-spin" />
                </div>
            ) : error ? (
                <div className="bg-red-50 dark:bg-red-900/10 p-6 rounded-xl border border-red-100 dark:border-red-900/20 text-center space-y-4">
                    <p className="text-red-600 dark:text-red-400 font-medium">{error}</p>
                    <button onClick={fetchSchedules} className="flex items-center justify-center gap-2 mx-auto text-primary hover:underline">
                        <RefreshCw size={16} /> Try Again
                    </button>
                </div>
            ) : schedules.length === 0 ? (
                <div className="text-center py-24 bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-xl">
                    <Calendar size={48} className="mx-auto text-light-subtle dark:text-dark-subtle mb-4" />
                    <p className="text-lg text-light-text dark:text-dark-text font-medium">No automated billing schedules created yet.</p>
                    <p className="text-light-subtle dark:text-dark-subtle mt-1">Tap 'New Schedule' to automate your rent and fixed charges generation.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {schedules.map(schedule => (
                        <motion.div 
                            key={schedule.id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            onClick={() => handleSchedulePress(schedule.id)}
                            className="bg-light-card dark:bg-dark-card p-6 rounded-xl shadow-sm border border-light-border dark:border-dark-border flex flex-col justify-between hover:shadow-md transition-all cursor-pointer group"
                        >
                            <div className="flex justify-between items-start mb-4">
                                <div>
                                    <h3 className="text-lg font-bold text-light-text dark:text-dark-text group-hover:text-primary transition-colors">
                                        {schedule.schedule_name}
                                    </h3>
                                    <span className={`inline-block mt-2 px-2 py-1 rounded-md text-xs font-bold tracking-widest ${
                                        schedule.status === 'ACTIVE' 
                                            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' 
                                            : 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
                                    }`}>
                                        {schedule.status}
                                    </span>
                                </div>
                                <div className="p-2 bg-primary/10 rounded-full">
                                    <Clock className="text-primary w-5 h-5" />
                                </div>
                            </div>

                            <div className="space-y-3 flex-1 mb-6">
                                <div className="flex items-center text-sm text-light-subtle dark:text-dark-subtle">
                                    <Building2 className="w-4 h-4 mr-2" />
                                    <span>{schedule.properties.length} Properties</span>
                                </div>
                                <div className="flex items-center text-sm text-light-subtle dark:text-dark-subtle">
                                    <Settings className="w-4 h-4 mr-2" />
                                    <span>Generates on Day {schedule.generation_day}</span>
                                </div>
                                <div className="flex items-center text-sm text-light-subtle dark:text-dark-subtle">
                                    <Calendar className="w-4 h-4 mr-2" />
                                    <span>Due {schedule.due_day} days after generation</span>
                                </div>
                            </div>

                            <div className="border-t border-light-border dark:border-dark-border pt-4">
                                <div className="flex justify-between items-center text-sm">
                                    <span className="text-light-subtle dark:text-dark-subtle">Avg Tenants</span>
                                    <span className="font-bold text-light-text dark:text-dark-text">{schedule.metrics?.total_tenants || 0}</span>
                                </div>
                                <div className="flex justify-between items-center text-sm mt-2">
                                    <span className="text-light-subtle dark:text-dark-subtle">Est. Total</span>
                                    <span className="font-bold text-primary">KES {schedule.metrics?.estimated_total?.toLocaleString() || 0}</span>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>
            )}
        </div>
    );
};
